import {
  PublicKey,
  TransactionInstruction,
  SystemProgram,
} from "@solana/web3.js";
import {
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID
} from "@solana/spl-token";
import { Buffer } from "buffer";
import { RTD_GAME_PROGRAM_ID } from "../constants";

/** The program ID for your Roll-the-Dice game */
export const RTD_PROGRAM_ID = new PublicKey(RTD_GAME_PROGRAM_ID);

/** Raw instruction discriminators from your IDL */
const IX_DS = {
  init_game: Buffer.from([251, 46, 12, 208, 184, 148, 157, 73]),
  init_game_income_account: Buffer.from([
        253,
        30,
        154,
        44,
        33,
        239,
        61,
        107
      ]),
  init_game_reward_account: Buffer.from([
        143,
        232,
        171,
        252,
        66,
        0,
        48,
        234
      ]),
  init_player: Buffer.from([114, 27, 219, 144, 50, 15, 228, 66]),
  play: Buffer.from([213, 157, 193, 142, 228, 56, 248, 150]),
  set_payout_map: Buffer.from([86, 187, 224, 31, 252, 205, 0, 238]),
  settle: Buffer.from([175, 42, 185, 87, 144, 131, 102, 212]),
  withdraw_from_reward_pool: Buffer.from([199, 150, 124, 62, 97, 214, 159, 61]),
  withdraw_income: Buffer.from([168, 219, 122, 53, 71, 191, 33, 99]),
};

/**
 * A single PayoutConfig, per your IDL:
 * {
 *    dice_1: u8,
 *    dice_2: u8,
 *    payout_type: u8,
 *    payout_value: u64
 * }
 */
export interface PayoutConfig {
  dice_1: number;       // 0-255
  dice_2: number;       // 0-255
  payout_type: number;  // 0 or 1
  payout_value: bigint; // prefer bigint for u64
}

/**
 * Helper to serialize a Vec<PayoutConfig> into a buffer
 */
function serializePayoutConfigs(configs: PayoutConfig[]): Buffer {
  // 4 bytes: length (LE)
  // each PayoutConfig = 1 + 1 + 1 + 8 = 11 bytes
  const length = configs.length;
  const out = Buffer.alloc(4 + length * 11);

  out.writeUInt32LE(length, 0);

  let offset = 4;
  configs.forEach(cfg => {
    out.writeUInt8(cfg.dice_1, offset); offset += 1;
    out.writeUInt8(cfg.dice_2, offset); offset += 1;
    out.writeUInt8(cfg.payout_type, offset); offset += 1;
    out.writeBigUInt64LE(BigInt(cfg.payout_value), offset);
    offset += 8;
  });
  return out;
}

/**
 * (UPDATED) 1) init_game instruction
 *
 * Breaking changes here:
 * - `game_state` is no longer marked as a signer, per the IDL.
 * - Added `game_state_authority`, `roll_mint`, and `reward_mint` to match the IDL.
 * - The account metas must match the IDL account order exactly.
 */
export function createInitGameInstruction(
  admin: PublicKey,
  gameState: PublicKey,
  gameStateAuthority: PublicKey,
): TransactionInstruction {
  // 1) Serialize the instruction data
  //    8 bytes disc
  const data = Buffer.alloc(8);
  IX_DS.init_game.copy(data, 0);

  // 2) Build account metas (in the order the IDL defines them)
  //    IDL order for init_game:
  //    1) admin (signer)
  //    2) game_state (not a signer, writable)
  //    3) game_state_authority (pda, not signer)
  //    8) token_program
  //    9) system_program
  //    10) associated_token_program
  const keys = [
    { pubkey: admin, isSigner: true, isWritable: true },
    { pubkey: gameState, isSigner: false, isWritable: true },
    { pubkey: gameStateAuthority, isSigner: false, isWritable: false },
    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    { pubkey: ASSOCIATED_TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
  ];

  return new TransactionInstruction({
    keys,
    programId: RTD_PROGRAM_ID,
    data,
  });
}

export function createInitGameIncomeAccountInstruction(
  admin: PublicKey,
  gameState: PublicKey,
  gameStateAuthority: PublicKey,
  rollIncome: PublicKey,
  rollMint: PublicKey,
): TransactionInstruction {
  // 1) Serialize the instruction data
  //    8 bytes disc
  const data = Buffer.alloc(8);
  IX_DS.init_game_income_account.copy(data, 0);

  // 2) Build account metas (in the order the IDL defines them)
  //    IDL order for init_game_accounts:
  //    1) admin (signer)
  //    2) game_state (not a signer, writable)
  //    3) game_state_authority (pda, not signer)
  //    4) roll_income (pda, writable)
  //    6) roll_mint
  //    8) token_program
  //    9) system_program
  //    10) associated_token_program
  const keys = [
    { pubkey: admin, isSigner: true, isWritable: true },
    { pubkey: gameState, isSigner: false, isWritable: true },
    { pubkey: gameStateAuthority, isSigner: false, isWritable: false },
    { pubkey: rollIncome, isSigner: false, isWritable: true },
    { pubkey: rollMint, isSigner: false, isWritable: false },
    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    { pubkey: ASSOCIATED_TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
  ];

  return new TransactionInstruction({
    keys,
    programId: RTD_PROGRAM_ID,
    data,
  });
}

export function createInitGameRewardAccountInstruction(
  admin: PublicKey,
  gameState: PublicKey,
  gameStateAuthority: PublicKey,
  rewardPool: PublicKey,
  rewardMint: PublicKey,
): TransactionInstruction {
  // 1) Serialize the instruction data
  //    8 bytes disc
  const data = Buffer.alloc(8);
  IX_DS.init_game_reward_account.copy(data, 0);

  // 2) Build account metas (in the order the IDL defines them)
  //    IDL order for init_game_reward_account:
  //    1) admin (signer)
  //    2) game_state (not a signer, writable)
  //    3) game_state_authority (pda, not signer)
  //    5) reward_pool (pda, writable)
  //    7) reward_mint
  //    8) token_program
  //    9) system_program
  //    10) associated_token_program
  const keys = [
    { pubkey: admin, isSigner: true, isWritable: true },
    { pubkey: gameState, isSigner: false, isWritable: true },
    { pubkey: gameStateAuthority, isSigner: false, isWritable: false },
    { pubkey: rewardPool, isSigner: false, isWritable: true },
    { pubkey: rewardMint, isSigner: false, isWritable: false },
    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    { pubkey: ASSOCIATED_TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
  ];

  return new TransactionInstruction({
    keys,
    programId: RTD_PROGRAM_ID,
    data,
  });
}

/**
 * 2) init_player instruction
 *    (no changes from your existing code; it already matches the IDL)
 */
export function createInitPlayerInstruction(
  player: PublicKey,
  playerState: PublicKey,
): TransactionInstruction {
  // 8 bytes = disc
  const data = Buffer.alloc(8);
  IX_DS.init_player.copy(data, 0);

  const keys = [
    { pubkey: player, isSigner: true, isWritable: true },
    { pubkey: playerState, isSigner: false, isWritable: true },
    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
  ];

  return new TransactionInstruction({
    keys,
    programId: RTD_PROGRAM_ID,
    data,
  });
}

/**
 * 3) play instruction
 *    (single argument => randomness_account (Pubkey))
 *    (unchanged)
 */
export function createPlayInstruction(
  playerState: PublicKey,
  player: PublicKey,
  userRollTokenAccount: PublicKey,
  rollIncome: PublicKey,
  randomnessAccountData: PublicKey,
  randomnessAccount: PublicKey
): TransactionInstruction {
  const data = Buffer.alloc(8 + 32);
  IX_DS.play.copy(data, 0);
  // Write the pubkey argument (32 bytes). 
  // A simpler approach is `randomnessAccount.toBytes()`, 
  // but either works as long as it’s 32 bytes in place.
  randomnessAccount.toBuffer().copy(data, 8);

  const keys = [
    { pubkey: playerState, isSigner: false, isWritable: true },
    { pubkey: player, isSigner: true, isWritable: false },
    { pubkey: userRollTokenAccount, isSigner: false, isWritable: true },
    { pubkey: rollIncome, isSigner: false, isWritable: true },
    { pubkey: randomnessAccountData, isSigner: false, isWritable: false },
    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
  ];

  return new TransactionInstruction({
    keys,
    programId: RTD_PROGRAM_ID,
    data,
  });
}

/**
 * 4) set_payout_map
 *    (unchanged; it already matches the IDL)
 */
export function createSetPayoutMapInstruction(
  gameState: PublicKey,
  admin: PublicKey,
  newPayoutMap: PayoutConfig[]
): TransactionInstruction {
  const serializedPayouts = serializePayoutConfigs(newPayoutMap);
  const data = Buffer.alloc(8 + serializedPayouts.length);
  IX_DS.set_payout_map.copy(data, 0);
  serializedPayouts.copy(data, 8);

  const keys = [
    { pubkey: gameState, isSigner: false, isWritable: true },
    { pubkey: admin, isSigner: true, isWritable: false },
  ];

  return new TransactionInstruction({
    keys,
    programId: RTD_PROGRAM_ID,
    data,
  });
}

/**
 * 5) settle
 *    (unchanged; matches IDL)
 */
export function createSettleInstruction(
  admin: PublicKey,
  gameState: PublicKey,
  playerState: PublicKey,
  randomnessAccountData: PublicKey,
  rewardPool: PublicKey,
  userMonopolyTokenAccount: PublicKey,
  gameStateAuthority: PublicKey
): TransactionInstruction {
  const data = Buffer.alloc(8);
  IX_DS.settle.copy(data, 0);

  const keys = [
    { pubkey: admin, isSigner: false, isWritable: false },
    { pubkey: gameState, isSigner: false, isWritable: true },
    { pubkey: playerState, isSigner: false, isWritable: true },
    { pubkey: randomnessAccountData, isSigner: false, isWritable: false },
    { pubkey: rewardPool, isSigner: false, isWritable: true },
    { pubkey: userMonopolyTokenAccount, isSigner: false, isWritable: true },
    { pubkey: gameStateAuthority, isSigner: false, isWritable: true },
    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
  ];

  return new TransactionInstruction({
    keys,
    programId: RTD_PROGRAM_ID,
    data,
  });
}

/**
 * 6) withdraw_from_reward_pool
 *    (unchanged; matches IDL)
 *    8 byte disc + 8 byte amount (u64 LE)
 */
export function createWithdrawFromRewardPoolInstruction(
  gameState: PublicKey,
  admin: PublicKey,
  gameStateAuthority: PublicKey,
  rewardPool: PublicKey,
  destinationAccount: PublicKey,
  amount: bigint
): TransactionInstruction {
  const data = Buffer.alloc(8 + 8);
  IX_DS.withdraw_from_reward_pool.copy(data, 0);
  data.writeBigUInt64LE(amount, 8);

  const keys = [
    { pubkey: gameState, isSigner: false, isWritable: true },
    { pubkey: admin, isSigner: true, isWritable: false },
    { pubkey: gameStateAuthority, isSigner: false, isWritable: true },
    { pubkey: rewardPool, isSigner: false, isWritable: true },
    { pubkey: destinationAccount, isSigner: false, isWritable: true },
    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
  ];

  return new TransactionInstruction({
    keys,
    programId: RTD_PROGRAM_ID,
    data,
  });
}

/**
 * 7) withdraw_income
 *    (unchanged; matches IDL)
 *    8 byte disc + 8 byte amount (u64 LE)
 */
export function createWithdrawIncomeInstruction(
  gameState: PublicKey,
  admin: PublicKey,
  gameStateAuthority: PublicKey,
  rollIncome: PublicKey,
  destinationAccount: PublicKey,
  amount: bigint
): TransactionInstruction {
  const data = Buffer.alloc(8 + 8);
  IX_DS.withdraw_income.copy(data, 0);
  data.writeBigUInt64LE(amount, 8);

  const keys = [
    { pubkey: gameState, isSigner: false, isWritable: true },
    { pubkey: admin, isSigner: true, isWritable: false },
    { pubkey: gameStateAuthority, isSigner: false, isWritable: true },
    { pubkey: rollIncome, isSigner: false, isWritable: true },
    { pubkey: destinationAccount, isSigner: false, isWritable: true },
    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
  ];

  return new TransactionInstruction({
    keys,
    programId: RTD_PROGRAM_ID,
    data,
  });
}
